import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { GigConsents } from '@lucky7ventures/bff-types';

import CloseButton from '@/components/UI/Buttons/CloseButton';
import { CheckBox } from '@/components/CheckBox';
import PromptMessage from '@/components/UI/PromptMessage';
import { BtnOneLineBlue } from '@/components/buttons/BtnOneLineBlue';
import {
  selectIsEmailConsented,
  selectIsTextMessageConsented,
  selectSmsEmailConsents,
  selectUpdateConsentsError,
  selectUpdateConsentsLoading,
} from '@/store/selectors/consentsSelectors';
import { updateConsents } from '@/store/actions/consentsActions';
import { BaseModalProps } from '@/features/modal/types';

export const SmsMailReminderModal = ({ closeModal }: BaseModalProps): JSX.Element => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const userConsents = useSelector(selectSmsEmailConsents);
  const isSmsConsented = useSelector(selectIsTextMessageConsented);
  const isEmailConsented = useSelector(selectIsEmailConsented);
  const isUpdateConsentsLoading = useSelector(selectUpdateConsentsLoading);
  const isUpdateConsentsError = useSelector(selectUpdateConsentsError);
  const [smsChecked, setSmsChecked] = useState(!!isSmsConsented);
  const [emailChecked, setEmailChecked] = useState(!!isEmailConsented);

  const handleSmsConsent = () => {
    setSmsChecked(!smsChecked);
  };

  const handleEmailConsent = () => {
    setEmailChecked(!emailChecked);
  };

  const handleUpdateConsents = () => {
    const updatedUserConsents = [...userConsents].map(consent => {
      return {
        consentId: consent.consentId,
        version: consent.version,
        consented: consent.name === GigConsents.TextMessage ? smsChecked : emailChecked,
      };
    });

    dispatch(updateConsents(updatedUserConsents));
    closeModal();
  };

  return (
    <div className="prose relative flex w-full max-w-sm flex-col overflow-hidden rounded-lg bg-slate-50 p-8 text-center">
      <CloseButton left onClick={closeModal} />
      <h1 className="mb-0.5 text-2xl">
        {intl.formatMessage({ id: 'smsMailReminderModal.header' })}
      </h1>
      <p className="text-sm font-medium leading-6">
        {intl.formatMessage({ id: 'smsMailReminderModal.text' })}
      </p>
      <div className="flex flex-col gap-4 rounded-lg bg-white px-4 pt-4 pb-2 shadow-md">
        <span className="text-sm font-semibold">
          {intl.formatMessage({ id: 'smsMailReminderModal.contact' })}
        </span>
        <div className="checkboxes flex items-center justify-around">
          <CheckBox
            className="mx-2"
            id="smsOptIn"
            value="checked"
            label={intl.formatMessage({ id: 'inputs.toggle.sms' })}
            initialCheck={smsChecked}
            handleChange={handleSmsConsent}
          />
          <CheckBox
            className="mx-2"
            id="emailOptIn"
            value="checked"
            label={intl.formatMessage({ id: 'inputs.toggle.email' })}
            initialCheck={emailChecked}
            handleChange={handleEmailConsent}
          />
        </div>
      </div>
      <div className="mt-6">
        <BtnOneLineBlue
          isLoading={isUpdateConsentsLoading}
          onClickHandler={handleUpdateConsents}
          text={intl.formatMessage({ id: 'misc.update' })}
        />
      </div>
      {isUpdateConsentsError && (
        <div className="mb-[-32px] mt-4">
          <PromptMessage type="error" message={intl.formatMessage({ id: 'error.support' })} />
        </div>
      )}
    </div>
  );
};
